import type { NavigateToInput } from '@realadvisor/common-types';
import { z } from 'zod';

import { DOCS_ARTIFACT_VERSION } from '../../../../docs/docsArtifactVersion';
import {
  extractLanguageFromUrl,
  fallbackLanguage,
  languagesList,
  parseLeadingSegment,
  sliceLeadingSegment,
} from '../../../../shared/LocaleContext';

import { IMPLEMENTED_CLIENT_TOOLS } from './unsupportedClientTool';

// Client-side execution of the agent's `navigate_to` tool. The model passes an
// in-app path (record, docs article, list with query params, …) and we navigate
// there. The only safety boundary is "stay inside this app": we resolve the
// path against the current origin and refuse anything that points elsewhere
// (external hosts, javascript:, protocol-relative //host). Over-navigating
// within the user's own CRM is not a security concern.
//
// The CRM URL patterns (lead -> /leads/{id}, …) are static, universal agent
// knowledge and live in the system prompt (crm-chat-api), not in this volatile
// per-request context.

const navigateInputSchema = z.object({ path: z.string().min(1) });

export type ResolveNavigateResult =
  | { ok: true; url: string }
  | { ok: false; error: string };

// Drop a leading locale segment so it isn't doubled by the router basename.
// `/en/docs/x?q=1` -> `/docs/x?q=1`; `/leads/123` -> unchanged.
const stripBasenameLocale = (pathname: string, suffix: string): string => {
  if (parseLeadingSegment(pathname, languagesList) != null) {
    return `/${sliceLeadingSegment(pathname, languagesList).replace(
      /\/$/,
      '',
    )}${suffix}`;
  }
  return `${pathname}${suffix}`;
};

export const resolveNavigatePath = (input: unknown): ResolveNavigateResult => {
  const parsed = navigateInputSchema.safeParse(input);
  if (!parsed.success) {
    return { ok: false, error: 'navigate_to requires a "path" string.' };
  }

  const raw = (parsed.data as NavigateToInput).path.trim();

  let resolved: URL;
  try {
    // Resolve relative paths and absolute URLs alike against the current origin.
    resolved = new URL(raw, window.location.origin);
  } catch {
    return { ok: false, error: `Invalid path: ${raw}` };
  }

  // The single guard: must stay on this origin. Catches external hosts,
  // protocol-relative `//host`, and schemes like `javascript:` (origin "null").
  if (resolved.origin !== window.location.origin) {
    return {
      ok: false,
      error: `Refusing to navigate to an external URL: ${raw}`,
    };
  }

  const url = stripBasenameLocale(
    resolved.pathname,
    `${resolved.search}${resolved.hash}`,
  );
  return { ok: true, url };
};

export type ChatClientContext = {
  /** Current in-app path, including the `/${language}` basename. */
  url: string;
  /** Active UI language (from the router basename). */
  locale: string;
  /** Current instant (ISO 8601, UTC). */
  localTime: string;
  /** User's IANA time zone, e.g. "Europe/Zurich". */
  timeZone: string;
  /**
   * sha256 of the docs content this CRM build bundles. Sent so the chat agent's
   * docs tools read the matching pre-built artifact in prod (see
   * crm-chat-api/utils/docs.ts).
   */
  docsVersion: string;
  /** Which app sent the request; the pro mobile app sends 'mobile'. */
  client: 'web';
  /** Client-side tools this build runs; gates the optional ones server-side. */
  clientTools: Array<string>;
  /** Physical screen size in CSS pixels. */
  screenWidth: number;
  screenHeight: number;
  /** Visible window size in CSS pixels. */
  viewportWidth: number;
  viewportHeight: number;
  /** devicePixelRatio. */
  dpr: number;
};

// Snapshot of the user's current client state, sent with each chat request so
// the agent's system prompt reflects where the user is and when.
export const buildChatClientContext = (): ChatClientContext => {
  return {
    url: `${window.location.pathname}${window.location.search}`,
    locale: extractLanguageFromUrl() ?? fallbackLanguage,
    localTime: new Date().toISOString(),
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    docsVersion: DOCS_ARTIFACT_VERSION,
    client: 'web',
    clientTools: [...IMPLEMENTED_CLIENT_TOOLS],
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    dpr: window.devicePixelRatio,
  };
};
